import { useMemo, useEffect, useRef } from 'react'
import _ from 'lodash'
import {
  Button,
  CopyButton,
  Body,
  noop,
} from '@panwds/react-ui'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from '../../../common/components/modal/Modal'
import { getAuthState } from '@sparky/framework'
import type { VFC, ReactNode } from 'react'
import type { RenderModalProps } from '@panwds/react-ui'
import type { AppManifest, Instance } from '@sparky/framework'
import { BUTTON_COPY, BUTTON_CLOSE, LABEL_PRODUCT_INFO } from '../../../tenancy/constants/LabelConstants'

export const AppInfoModal: VFC<RenderModalProps> = ({
  closeModal,
  isOpen,
  instance,
  app,
}) => {
  const [app_display_name, table, copyText, copyButton] = useMemo(() => {
    if (!instance) {
      return []
    }
    const {
      tsg_id,
      app_id,
      tenant_id,
      tenant_instance_name,
      csp_tenant_id,
      serial_number,
      support_account_id,
      support_account_name,
      region,
      platform_region,
      status,
      provisioning_status,
      provisioning_message,
      url,
    } = instance as Instance
    const { display_name: app_display_name } = app as AppManifest
    const tsg_name = getAuthState()?.tsgs?.get(tsg_id!)?.display_name || null
    const info = {
      tsg_id,
      app_id,
      app_display_name,
      tsg_name,
      tenant_id,
      tenant_instance_name,
      csp_tenant_id,
      serial_number,
      support_account_id,
      support_account_name,
      region,
      platform_region,
      provisioning_status,
      provisioning_message,
      status,
      url,
      referrer: window.location.href,
      generated: Date.now(),
    }
    const displayRegion = (region: string = '') => (region.length === 2 ? region.toUpperCase() : _.capitalize(region))
    const regions = platform_region && region && platform_region !== region ? <>
      {displayRegion(platform_region)} / {displayRegion(region)}
    </> : (displayRegion(platform_region || region))
    const isRenameAllowed = (app as AppManifest)?.flags?.is_rename_allowed
    const fields = [
      ['Tenant', `${tsg_name} - ${tsg_id}`],
      ['Product', app_display_name],
      ['Instance ID', `${tenant_id}${csp_tenant_id && csp_tenant_id !== tenant_id ? ` (from: ${csp_tenant_id})` : ''}`],
      isRenameAllowed ? ['Instance Name', tenant_instance_name] : [],
      ['Serial Number', serial_number],
      ['Support Account', support_account_name ? `${support_account_name} - ${support_account_id}` : String(support_account_id)],
      ['Data Region', regions],
      ['Instance Status', _.capitalize(status)],
      ['Provisioning Status', _.capitalize(provisioning_status)],
    ].filter(field => field[1]) as [string, ReactNode][]
    const copyText = [
      ...fields,
      ['DETAILS', JSON.stringify(info, null, 2)]
    ].map(([name, value]) => `${name}: ${value}`).join('\n')
    const table = <table className='cm-w-full cm-mt-2 cm-mb-4' style={{ tableLayout: 'fixed' }}>
      <tbody>
        {fields.map(([name, value]) => (
          <tr key={name}>
            <td className='cm-align-top'><strong>{name}</strong></td>
            <td className='cm-align-top' style={{ width: '75%' }}>{value}</td>
          </tr>
        ))}
        {provisioning_message ? <tr key='provisioning_message'>
          <td className='cm-align-top'><strong>Provisioning Message</strong></td>
          <td className='cm-align-top' style={{ width: '75%' }}>
            <pre key='provisioning_message' className='cm-m-0' style={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word',
              maxHeight: 200,
              overflowY: 'auto',
            }}>
              {_.isString(provisioning_message) ? provisioning_message : JSON.stringify(provisioning_message, null, 2)}
            </pre>
          </td>
        </tr> : null}
      </tbody>
    </table>
    const copyButton = <CopyButton
      text={copyText}
      appearance='secondary'
    >{BUTTON_COPY}</CopyButton>
    return [app_display_name, table, copyText, copyButton]
  }, [app, instance])

  const copyRef = useRef(copyText)
  copyRef.current = copyText

  useEffect(() => {
    const copyListener = (e: ClipboardEvent) => {
      const selection = document.getSelection()?.toString()
      const override = !selection?.length || (
        selection.length > 10 && selection.includes('Provisioning Status') && selection.includes('Instance Status')
      )
      if (copyRef.current && override) {
        e.preventDefault()
        e.clipboardData?.setData('text/plain', copyRef.current)
      }
    }
    document.addEventListener('copy', copyListener)
    return () => {
      document.removeEventListener('copy', copyListener)
    }
  }, [])

  const help = <Body addClassName='cm-mt-2 cm-mb-2'>
    If you’ve filed a support case for this product, please copy and attach to the <br /><a
      href='https://supportcases.paloaltonetworks.com/'
      target='_blank'
      rel='noopener noreferrer'
    >Support Case</a> to facilitate resolution.
  </Body>
  const closeButton = (
    <Button onClick={closeModal} appearance="primary">
      {BUTTON_CLOSE}
    </Button>
  )
  return (
    <Modal onClose={closeModal} isOpen={isOpen} size='md' center>
      <ModalHeader
        title={<>{LABEL_PRODUCT_INFO} - {app_display_name}</>}
      />
      <ModalBody addClassName='cm-pb-0'>
        {table}
        {help}
      </ModalBody>
      <ModalFooter
        appearance='clear'
        addClassName='cm-justify-between'
        footerMessage={copyButton}
        secondaryContent={closeButton}
      />
    </Modal>
  )
}

export const useAppInfoModule = (instance: Instance | undefined, app: AppManifest) => {
  const { openModal, closeModal } = useModal(AppInfoModal)
  return useMemo(() => ({
    openModal: instance ? openModal.bind(undefined, { instance, app }) : noop,
    closeModal,
  }), [openModal, instance, app, closeModal])
}
